import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
const { connect } = require('react-redux');
import DatePicker, { CalendarContainer, ReactDatePickerProps } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
const classNames = require('classnames');
import { IStore } from 'redux/IStore';
import { ILanguage } from 'models/common';
import renderIcon, { IconName } from 'helpers/renderIcon';
import { addURLSearchParams, deleteURLSearchParams, readURLSearchParams } from 'helpers/common';
import { withRouter } from 'react-router';
import { sl, it, hr, enUS } from 'date-fns/esm/locale';
import { IUserLanguage } from 'models/user/userlanguage';
import moment = require('moment');
import { momentWithTimezone } from 'redux/modules/shop/helpers';

const DATEPICKER_USAGE = {
	portal: '',
	admin: 'portalAdminDatePicker',
};

export interface IDatePickerProps extends Omit<ReactDatePickerProps, 'onChange'> {
	usage?: 'portal' | 'admin';
	input: any;
	meta?: any;
	label?: string;
	pickedDate?: any;
	onKeyDown?: (e: any) => void;
	description?: string;
	information?: string;

	userLanguage?: IUserLanguage;
	languageDomainValues?: ILanguage[];

	iconName?: IconName;
	iconClassName?: string;
	iconAction?: () => void | undefined;
	customIconSizeWidth?: number;
	customIconSizeHeight?: number;

	highlightWhenValueSet?: boolean;
	putSelectedValueInUrlQuery?: string; // url prop key name

	customCalendarContainerEl?: JSX.Element;

	location?: any; // obtained with withRouter

	t?: (key: string) => string;
	minDate?: Date;
	maxDate?: Date;
	disabled?: boolean;
	timezone?: string;
	hideYearArrows?: boolean;
	hideMonthArrows?: boolean;
	isYearInputable?: boolean;

	customValueChangeFunction?: () => void;
}
interface IState {
	selectedDate?: Date;
	dropdownMode: string;
}

export const setLocalZone = (date: Date | string, timezone: string) => {
	const dateWithoutZone = momentWithTimezone(timezone, date).format('YYYY-MM-DDTHH:mm:ss.SSS');
	const localZone = moment(dateWithoutZone).format('Z');
	const dateWithLocalZone = [dateWithoutZone, localZone].join('');

	return new Date(dateWithLocalZone);
};

export const setOtherZone = (date: Date, timezone: string) => {
	const dateWithoutZone = moment(date).format('YYYY-MM-DDTHH:mm:ss.SSS');
	const otherZone = momentWithTimezone(timezone, date).format('Z');
	const dateWithOtherZone = [dateWithoutZone, otherZone].join('');

	return new Date(dateWithOtherZone);
};
@connect((state: IStore) => {
	return {
		userLanguage: state.userLanguage,
		languageDomainValues: state.domainValues.languages,
	};
})
@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class BookingDatePickerW extends React.Component<IDatePickerProps, IState> {
	constructor(props) {
		super(props);
		this.state = {
			selectedDate: props.input.value ? new Date(props.input.value) : undefined,
			dropdownMode: 'select',
		};
		this.handleClearSelectedValue = this.handleClearSelectedValue.bind(this);
	}
	datepickerRef = React.createRef<DatePicker>();

	private get culture(): string | undefined {
		if (this.props.languageDomainValues && this.props.userLanguage && Array.isArray(this.props.languageDomainValues)) {
			const languageDV = this.props.userLanguage;
			return languageDV ? languageDV.culture.substring(0, 2) : undefined;
		}
	}

	//////////////////////////////////////////////
	// Apply values from URL query on mount - if any
	//////////////////////////////////////////////
	public componentDidMount(): void {
		if (this.props.putSelectedValueInUrlQuery) {
			const extractedParamValue = readURLSearchParams(this.props.putSelectedValueInUrlQuery);
			if (extractedParamValue) {
				this.setState({
					selectedDate: new Date(extractedParamValue),
				});
				if (this.props.input?.onChange) {
					this.props.input?.onChange(extractedParamValue);
				}
			}
		}
	}
	//////////////////////////////////////////////

	public componentDidUpdate(prevProps: IDatePickerProps) {
		if (prevProps.input.value !== this.props.input.value) {
			const selectedDate = this.props.input.value ? new Date(this.props.input.value) : undefined;
			if (selectedDate?.getTime() !== this.state.selectedDate?.getTime()) {
				this.setState({ selectedDate });
			}
		}
		if (
			prevProps.location !== this.props.location &&
			this.props.putSelectedValueInUrlQuery &&
			!readURLSearchParams(this.props.putSelectedValueInUrlQuery)
		) {
			this.setState({ selectedDate: undefined });
		}
	}

	private handleChange = (date) => {
		try {
			if (!date) {
				date = momentWithTimezone(this.props.timezone).startOf('day').toDate();
			}
			// set the timezone of the office
			date = this.props.timezone ? setOtherZone(date, this.props.timezone) : date;
			let value: Date = date && !isNaN(date) ? date : undefined;

			// Add values as URL search params
			if (this.props.putSelectedValueInUrlQuery && value) {
				addURLSearchParams(this.props.putSelectedValueInUrlQuery, value.toISOString());
			} else if (this.props.putSelectedValueInUrlQuery && !value) {
				deleteURLSearchParams([this.props.putSelectedValueInUrlQuery]);
			}

			if (!this.props.showTimeSelect && value) {
				// Must be Date object, else returns Invalid Date
				value = date ? momentWithTimezone(this.props.timezone, date).startOf('day').toDate() : undefined;
			}

			this.setState({
				selectedDate: value,
			});

			if (value && this.props.input?.onChange) {
				if (this.props?.customValueChangeFunction) {
					this.props.customValueChangeFunction();
				}
				this.props.input.onChange(value.toISOString());
			}
		} catch (e) {
			// just in case anything happens, so the site does not crash
			console.error('An exception in booking date picker has been caught');
			console.error(e);
		}
	};

	private handleClearSelectedValue(e: React.MouseEvent) {
		if (e) {
			e.stopPropagation();
		}
		if (this.state.selectedDate) {
			this.handleChange(undefined);
		}
		deleteURLSearchParams([this.props.putSelectedValueInUrlQuery]);
	}

	uniqId = 'input-' + Math.random();

	public render() {
		const {
			inline,
			pickedDate,
			information,
			input,
			t,
			readOnly,
			iconAction,
			customIconSizeHeight,
			customIconSizeWidth,
			highlightWhenValueSet,
			customCalendarContainerEl,
			showTimeSelectOnly,
		} = this.props;
		let { iconName, iconClassName } = this.props;
		if (!iconName && showTimeSelectOnly) {
			iconName = 'dropdown';
			iconClassName = 'dateDropdownIcon pointer';
		}
		const months = [
			t('BSPortal_Common_MonthName_January'),
			t('BSPortal_Common_MonthName_February'),
			t('BSPortal_Common_MonthName_March'),
			t('BSPortal_Common_MonthName_April'),
			t('BSPortal_Common_MonthName_May'),
			t('BSPortal_Common_MonthName_June'),
			t('BSPortal_Common_MonthName_July'),
			t('BSPortal_Common_MonthName_August'),
			t('BSPortal_Common_MonthName_September'),
			t('BSPortal_Common_MonthName_October'),
			t('BSPortal_Common_MonthName_November'),
			t('BSPortal_Common_MonthName_December'),
		];

		let informationContainer: JSX.Element = null;
		if (typeof information !== 'undefined' && information !== null) {
			informationContainer = <div className="informationBlock">{information}</div>;
		}

		let pickedDateContent = null;
		if (inline && pickedDate) {
			pickedDateContent = <input readOnly={true} value={pickedDate} type="text" className="inputField" />;
		}

		const usage = this.props.usage ? DATEPICKER_USAGE[this.props.usage] : DATEPICKER_USAGE.portal;

		let description = this.props.description;
		let status = '';
		if (this.props.meta?.touched) {
			if (this.props.meta?.warning) {
				status = 'withWarning';
				description = this.props.meta.warning;
			}
			if (this.props.meta?.error) {
				description = this.props.meta.error;
				status = 'withError';
			}
		}

		const innerDateValue = (s: string, changeYear: (year: number) => void) => {
			const regex = /[^0-9]+/gm;
			if (s.length >= 4) {
				s = s.substring(0, 4);
			}
			if (!s.match(regex)) {
				changeYear(Number(s));
				return;
			}
			const splitString = s.split('');
			const match = splitString.findIndex((value) => regex.test(value));
			splitString.splice(match, 1);
			return;
		};
		const handleYearClick = (e: React.MouseEvent<HTMLInputElement>) => {
			e.currentTarget.focus();
			e.currentTarget.select();
		};

		const calendarContainer = ({ className, children }) => {
			if (customCalendarContainerEl) {
				return (
					<CalendarContainer className={className + ' customContainer'}>
						<div className="customContainerEl">{customCalendarContainerEl}</div>
						<div style={{ position: 'relative' }}>{children}</div>
					</CalendarContainer>
				);
			} else {
				return <CalendarContainer className={className}>{children}</CalendarContainer>;
			}
		};

		let language = enUS;
		switch (
			this.culture // Fix for BUG-7514 (A locale object was not found for the provided string ["sl"]),
		) {
			// check out https://github.com/Hacker0x01/react-datepicker/issues/1815#issuecomment-513215416
			case 'sl':
				language = sl;
				break;
			case 'hr':
				language = hr;
				break;
			case 'it':
				language = it;
				break;
		}

		const inputId = this.uniqId + '-' + input.name;

		return (
			<div
				className={classNames(
					'bookingFieldGroup',
					this.props.className,
					usage,
					status,
					{ inline, value: this.state.selectedDate || pickedDate },
					readOnly ? 'readOnly' : '',
				)}
			>
				{informationContainer}
				<div
					onClick={(e: React.SyntheticEvent<HTMLDivElement>) => {
						const target = e.target as HTMLDivElement;
						if (target && target.classList.contains('inputHolder')) this.datepickerRef.current?.setFocus?.();
					}}
					className={`inputHolder cursor-text-hover ${
						highlightWhenValueSet && this.state.selectedDate ? 'highlight' : ''
					}`}
				>
					{pickedDateContent}

					<DatePicker
						locale={language}
						calendarStartDay={1}
						{...this.props} // this should be before minTime so minTime from props does not override
						minTime={
							this.props.minTime && this.props.timezone
								? setLocalZone(this.props.minTime, this.props.timezone)
								: this.props.minTime
						}
						maxDate={this.props.maxDate ? this.props.maxDate : undefined}
						maxTime={
							this.props.maxTime && this.props.timezone
								? setLocalZone(this.props.maxTime, this.props.timezone)
								: this.props.maxTime
						}
						minDate={this.props.minDate ? this.props.minDate : undefined}
						className={classNames('inputField', {
							hasIconRight: iconName,
						})}
						id={inputId}
						dateFormat={this.props.dateFormat || 'dd.MM.yyyy'}
						onChange={this.handleChange}
						selected={
							this.state.selectedDate && this.props.timezone
								? setLocalZone(this.state.selectedDate, this.props.timezone)
								: this.state.selectedDate
						}
						disabledKeyboardNavigation={true}
						ref={this.datepickerRef}
						calendarContainer={calendarContainer}
						renderCustomHeader={
							this.props.showTimeSelectOnly
								? undefined
								: ({
										date,
										changeYear,
										decreaseMonth,
										increaseMonth,
										decreaseYear,
										increaseYear,
										prevMonthButtonDisabled,
										nextMonthButtonDisabled,
										prevYearButtonDisabled,
										nextYearButtonDisabled,
								  }) => (
										<div
											style={{
												display: 'flex',
												justifyContent: 'center',
											}}
										>
											<div className="d-flex flex-row align-items-center">
												{!this.props.hideYearArrows && (
													<button
														type="button"
														onClick={decreaseYear}
														disabled={prevYearButtonDisabled}
														className="pointer"
													>
														{renderIcon('arrow_left_round', 18, 18, 'datePickerArrow')}
													</button>
												)}
												{!this.props.hideMonthArrows && (
													<button
														type="button"
														onClick={decreaseMonth}
														disabled={prevMonthButtonDisabled}
														className="pointer"
													>
														{renderIcon('arrow_left_round', 18, 18, 'datePickerArrow')}
													</button>
												)}
											</div>
											<div className="d-flex flex-row align-items-center">
												<p className="month">{months[date.getMonth()].slice(0, 3)}</p>
												{this.props.isYearInputable ? (
													<input
														className="year"
														value={date.getFullYear()}
														onChange={({ target: { value } }) => innerDateValue(value, changeYear)}
														onClick={handleYearClick}
													/>
												) : (
													<p className="year">{date.getFullYear()}</p>
												)}
											</div>
											<div className="d-flex flex-row align-items-center">
												{!this.props.hideMonthArrows && (
													<button
														type="button"
														onClick={increaseMonth}
														disabled={nextMonthButtonDisabled}
														className="pointer"
													>
														{renderIcon('arrow_right_round', 18, 18, 'datePickerArrow')}
													</button>
												)}
												{!this.props.hideYearArrows && (
													<button
														type="button"
														onClick={increaseYear}
														disabled={nextYearButtonDisabled}
														className="pointer"
													>
														{renderIcon('arrow_right_round', 18, 18, 'datePickerArrow')}
													</button>
												)}
											</div>
										</div>
								  )
						}
					/>

					{iconName
						? this.state.selectedDate && !showTimeSelectOnly
							? renderIcon(
									'cancel_search',
									undefined,
									undefined,
									'clearFieldIcon pointer',
									this.handleClearSelectedValue,
							  )
							: renderIcon(iconName, customIconSizeWidth || 16, customIconSizeHeight || 16, iconClassName, iconAction)
						: null}
				</div>

				{description ? <span className="note">{description}</span> : null}
			</div>
		);
	}
}

export const BookingDatePicker = withRouter(BookingDatePickerW);
